/**
 * Jane in jeder App — die EINE Ursprungsliste je Umgebung (Spec
 * `docs/superpowers/specs/2026-10-08-jane-in-jeder-app-design.md` §2.3, §4, §5).
 *
 * Aus dieser Datei leiten sich ab: `frame-ancestors` von ai-chat (welche Wirte den
 * Chat einbetten duerfen), `frame-src` und `Permissions-Policy` der Wirte (welcher
 * Chat-Ursprung eingebettet wird), die Ursprungspruefung beider Bruecken-Enden
 * (`jw-jane/1`) und das Navigationsziel von `app_oeffnen`. Eine zweite Liste
 * daneben ist ein Fehler.
 *
 * **Ausgeschrieben, nie ein Muster.** Unter `*.janeway.io` liegen Kundenseiten,
 * `*.janewaylabs.com` hat Wildcard-DNS (`beta.`, eine fremde `ai-chat.`). Ein
 * Suffix-Vergleich wuerde jeder davon den Chat samt Mikrofon und Sitzung geben.
 *
 * **Nicht in der Liste, mit Grund:**
 * - `studio` — liefert nutzererzeugtes HTML auf dem eigenen Ursprung aus
 *   (`/teilen/<token>`, `/projekt/<id>/inhalt`). Ein solcher Ursprung darf nie Wirt sein.
 * - `docs` (api-docs) — eingefroren seit 2026-09-28.
 * - `auth`, `status` — keine Huelle.
 * - `chat` ist der Chat-Ursprung selbst, nie ein Wirt (`AppLayout jane={false}`).
 *
 * **Prod ist leer bis zur Freigabe durch den Betreiber** (`PROD-FREIGABE.md`). Die
 * Freigabe ist dann diese Liste plus Test — keine Suche ueber 22 Apps.
 */

export type JaneUmgebung = "labs" | "prod" | "lokal";

/** Schluessel der Apps mit Huelle, die Jane tragen duerfen (= Subdomain in Labs). */
export const JANE_WIRT_APPS = [
  "admin",
  "affiliate",
  "automations",
  "brain",
  "calendar",
  "cashflow",
  "contacts",
  "crm",
  "dictum",
  "drive",
  "gui",
  "hosting",
  "hub",
  "immo",
  "legal",
  "mail",
  "maps",
  "meet",
  "messenger",
  "notes",
  "pass",
  "photo",
  "tasks",
  "weather",
] as const;

export type JaneWirtApp = (typeof JANE_WIRT_APPS)[number];

export interface JaneUrspruenge {
  /** Ursprung von janeway-ai-chat, `null` = in dieser Umgebung gibt es kein Jane. */
  chat: string | null;
  /** Wirts-App → exakter Ursprung (Schema + Host + ggf. Port, ohne Pfad). */
  wirte: Readonly<Partial<Record<JaneWirtApp, string>>>;
}

function labs(): Record<JaneWirtApp, string> {
  const r = {} as Record<JaneWirtApp, string>;
  for (const app of JANE_WIRT_APPS) r[app] = `https://${app}.janewaylabs.com`;
  return r;
}

/** Dev-Ports aus den `dev`-Skripten der jeweiligen `package.json` (2026-10-08 gelesen). */
const LOKALE_PORTS: Record<JaneWirtApp, number> = {
  admin: 4032,
  affiliate: 4040,
  automations: 4027,
  brain: 4053,
  calendar: 4023,
  cashflow: 4054,
  contacts: 4021,
  crm: 4020,
  dictum: 4029,
  drive: 4030,
  gui: 4031,
  hosting: 4051,
  hub: 4039,
  immo: 4043,
  legal: 4056,
  mail: 4046,
  maps: 4066,
  meet: 4049,
  messenger: 4047,
  notes: 4024,
  pass: 4062,
  photo: 4072,
  tasks: 4022,
  weather: 4067,
};

function lokal(): Record<JaneWirtApp, string> {
  const r = {} as Record<JaneWirtApp, string>;
  for (const app of JANE_WIRT_APPS) r[app] = `http://localhost:${LOKALE_PORTS[app]}`;
  return r;
}

/**
 * Die Liste. `lokal` gilt NUR gegen einen lokalen ai-chat (`localhost:4026`): eine
 * lokale App, die `chat.janewaylabs.com` einbettet, ist Cross-Site (Lax-Sitzung fehlt,
 * Speicher partitioniert), und `localhost` in der Labs-Liste oeffnete Labs fuer jeden
 * lokalen Prozess. Ein Waechter haelt `localhost` aus `labs` heraus.
 */
export const JANE_URSPRUENGE: Readonly<Record<JaneUmgebung, JaneUrspruenge>> = {
  labs: { chat: "https://chat.janewaylabs.com", wirte: labs() },
  prod: { chat: null, wirte: {} },
  lokal: { chat: "http://localhost:4026", wirte: lokal() },
};

/** Wirts-Ursprung → Umgebung und App, exakt verglichen. `null` = kein Wirt. */
export function wirtVonUrsprung(ursprung: string): { umgebung: JaneUmgebung; app: JaneWirtApp } | null {
  for (const umgebung of Object.keys(JANE_URSPRUENGE) as JaneUmgebung[]) {
    const wirte = JANE_URSPRUENGE[umgebung].wirte;
    for (const app of Object.keys(wirte) as JaneWirtApp[]) {
      if (wirte[app] === ursprung) return { umgebung, app };
    }
  }
  return null;
}

/** Umgebung, in der `ursprung` der Chat-Ursprung ist; `null` = kein Chat-Ursprung. */
export function umgebungVonChatUrsprung(ursprung: string): JaneUmgebung | null {
  for (const umgebung of Object.keys(JANE_URSPRUENGE) as JaneUmgebung[]) {
    const chat = JANE_URSPRUENGE[umgebung].chat;
    if (chat !== null && chat === ursprung) return umgebung;
  }
  return null;
}

/** Ursprung der Ziel-App fuer `app_oeffnen`; `null` = in dieser Umgebung nicht erlaubt. */
export function ursprungVonApp(app: string, umgebung: JaneUmgebung): string | null {
  const wirte = JANE_URSPRUENGE[umgebung].wirte as Record<string, string | undefined>;
  return Object.prototype.hasOwnProperty.call(wirte, app) ? (wirte[app] ?? null) : null;
}

/** Alle Wirts-Ursprünge einer Umgebung — Quelle fuer ai-chats `frame-ancestors`. */
export function wirtsUrspruenge(umgebung: JaneUmgebung): string[] {
  return Object.values(JANE_URSPRUENGE[umgebung].wirte).filter((u): u is string => typeof u === "string");
}

export interface JaneAktivOptionen {
  /**
   * Lokaler Opt-in. Ohne ihn ist Jane auch auf `localhost` aus — ein Entwickler
   * schaltet es ausdruecklich ein (`janeLokalOptIn()`), nie die Umgebung von selbst.
   */
  lokalOptIn?: boolean;
}

/**
 * Browser-Schalter (Spec §4): darf die Huelle auf diesem Ursprung den Jane-Knopf
 * zeigen und das iframe montieren?
 *
 * Wahr NUR fuer einen Wirts-Ursprung der Labs-Liste, lokal zusaetzlich nur mit
 * Opt-in. Prod ist immer falsch, solange die Prod-Liste leer ist — unabhaengig vom
 * Bau, denn die Entscheidung faellt am Ursprung zur Laufzeit, nicht an einer
 * eingebackenen Variable. Der Chat-Ursprung selbst ist kein Wirt.
 */
export function janeAktiv(ursprung: string, optionen: JaneAktivOptionen = {}): boolean {
  const wirt = wirtVonUrsprung(ursprung);
  if (!wirt) return false;
  if (wirt.umgebung === "labs") return true;
  if (wirt.umgebung === "lokal") return optionen.lokalOptIn === true;
  return false;
}

/** Schluessel im `localStorage` des Wirts fuer den lokalen Opt-in; Wert `"1"`. */
export const JANE_LOKAL_OPT_IN_SCHLUESSEL = "jw-jane-lokal";

/** Liest den lokalen Opt-in im Browser. Ohne `window` oder bei gesperrtem Speicher: aus. */
export function janeLokalOptIn(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(JANE_LOKAL_OPT_IN_SCHLUESSEL) === "1";
  } catch {
    return false;
  }
}

/**
 * Chat-Ursprung, den DIESER Wirt in `frame-src` und `Permissions-Policy` nennen muss;
 * `null` = nichts aendern (Prod-Host, Chat selbst, fremder Host, kein Host).
 *
 * Entschieden wird am `Host`-Kopf der Anfrage und exakt gegen die Liste — nicht am Bau
 * (`standalone` friert `next.config` ein) und nicht an `JANE_UMGEBUNG`, die in den
 * Wirts-Containern nicht steht (Spec §4 setzt sie nur in ai-chat, automations, Engine,
 * ai-core). Ein erfundener `Host` weitet nur die Antwort an den Absender.
 */
export function janeChatUrsprungFuerHost(host: string | null | undefined): string | null {
  if (!host) return null;
  const h = host.toLowerCase();
  const wirt = wirtVonUrsprung(`https://${h}`) ?? wirtVonUrsprung(`http://${h}`);
  return wirt ? JANE_URSPRUENGE[wirt.umgebung].chat : null;
}
